import React from 'react';
import AlignedPhotoFrame from '../AlignedPhotoFrame';
import { CardShell, BrandFooter, PhotoTag, scaleFor, ORANGE } from '../CardBits';

// Template C — Frozen Slider.
// One body split down the middle at whatever wipe position the viewer was left
// at. The composite is ALWAYS 9:16 and centred; extra width is freed for type.
//
// EXPORT SAFETY: the wipe uses width + overflow:hidden, NOT clip-path —
// html2canvas@1.4.1 ignores clip-path entirely and would render the whole
// before-photo over the after-photo. See lib/photoRender.js.
export default function WipeCard({ model, W, H, form }) {
  const s = scaleFor(H);
  const compW = Math.min(W, H * 9 / 16);
  const compLeft = (W - compW) / 2;
  const wipeX = compW * Math.min(100, Math.max(0, model.sliderPos)) / 100;
  const wide = form === 'wide';

  const frame = (side) => (
    <AlignedPhotoFrame
      url={side.url}
      backdropUrl={side.backdropUrl}
      canonical={side.canonical}
      manual={side.manual}
      natural={side.natural}
      slotW={compW}
      slotH={H}
    />
  );

  return (
    <CardShell W={W} H={H} background="#000000">
      {/* Letterbox fill behind the composite */}
      {compW < W && model.after.backdropUrl && (
        <img
          src={model.after.backdropUrl}
          alt=""
          aria-hidden="true"
          style={{ position: 'absolute', left: 0, top: 0, width: W, height: H, opacity: 0.5 }}
        />
      )}

      <div style={{ position: 'absolute', left: compLeft, top: 0, width: compW, height: H }}>
        {frame(model.after)}

        {/* The wipe: a hard-width window over the before photo. */}
        <div style={{ position: 'absolute', left: 0, top: 0, width: wipeX, height: H, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', left: 0, top: 0, width: compW, height: H }}>
            {frame(model.before)}
          </div>
        </div>

        <div
          style={{
            position: 'absolute',
            left: wipeX - 1.5 * s,
            top: 0,
            width: 3 * s,
            height: H,
            background: 'rgba(255,255,255,0.9)',
          }}
        />

        <PhotoTag tone="dark" s={s} style={{ left: 20 * s, top: 20 * s }}>
          {model.labels.before}{model.before.weightLabel ? ` · ${model.before.weightLabel}` : ''}
        </PhotoTag>
        <PhotoTag tone="accent" s={s} style={{ right: 20 * s, top: 20 * s }}>
          {model.labels.after}{model.after.weightLabel ? ` · ${model.after.weightLabel}` : ''}
        </PhotoTag>
      </div>

      {/* Type: beside the composite in wide form, over a scrim otherwise. */}
      {wide ? (
        <div style={{ position: 'absolute', left: 50 * s, top: '50%', width: compLeft - 80 * s, marginTop: -140 * s }}>
          <div style={{ fontSize: 26 * s, letterSpacing: 4 * s, color: ORANGE, fontWeight: 700 }}>
            {model.hero.sub}
          </div>
          <div style={{ fontSize: 132 * s, fontWeight: 800, lineHeight: 0.92, letterSpacing: -2 * s }}>
            {model.hero.value}
          </div>
          <div style={{ fontSize: 44 * s, fontWeight: 800, opacity: 0.5, letterSpacing: 4 * s }}>
            {model.hero.unit ? model.hero.unit.toUpperCase() : model.poseLabel}
          </div>
          {model.showNumbers && model.before.weightLabel && model.after.weightLabel && (
            <div style={{ fontSize: 28 * s, opacity: 0.5, marginTop: 16 * s }}>
              {model.before.weightLabel} &rarr; {model.after.weightLabel}
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            style={{
              position: 'absolute', left: 0, right: 0, bottom: 0, height: 380 * s,
              background: 'linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0.92))',
            }}
          />
          <div style={{ position: 'absolute', left: 40 * s, right: 40 * s, bottom: 110 * s }}>
            <div style={{ fontSize: 26 * s, letterSpacing: 4 * s, color: ORANGE, fontWeight: 700 }}>
              {model.hero.sub}
            </div>
            <div style={{ fontSize: 116 * s, fontWeight: 800, lineHeight: 1 }}>
              {model.hero.value}
              {model.hero.unit && <span style={{ fontSize: 48 * s, opacity: 0.6 }}>{model.hero.unit}</span>}
            </div>
          </div>
        </>
      )}

      <div style={{ position: 'absolute', left: 40 * s, right: 40 * s, bottom: 34 * s }}>
        <BrandFooter handleLabel={model.handleLabel} s={s} bordered={false} />
      </div>
    </CardShell>
  );
}
